// Delete empty sessions. The success toast depends on `num_deleted`, so it
// crosses as a port; the error copy is static meta.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { logger, sanitizeError } from '@heidi/runtime/logger';

import { GET_SESSION_BY_ID } from '../../contracts';
import { deleteEmptySessions } from '../api/deletion';
import {
  invalidateSessionsQuery,
  useCancelSessionsQuery,
} from '../query/sessionsListCache';

const DELETE_EMPTY_ERROR_META = {
  errorToast: {
    title: 'Backend Error',
    description: 'useMutateDeleteEmptySessions',
  },
} as const;

export function useMutateDeleteEmptySessions({
  onEmptySessionsDeleted,
}: {
  /** Fired with `num_deleted` where the success/empty toasts branched. */
  onEmptySessionsDeleted?: (numDeleted: number) => void;
} = {}) {
  const queryClient = useQueryClient();
  const { cancelSessionsQuery } = useCancelSessionsQuery();

  return useMutation({
    mutationKey: ['DeleteEmptySessions'],
    meta: DELETE_EMPTY_ERROR_META,
    mutationFn: async () => {
      return await deleteEmptySessions();
    },

    onMutate: async () => {
      await cancelSessionsQuery();
    },

    onSuccess: async (result) => {
      await invalidateSessionsQuery();

      const numDeleted = result.data.num_deleted;
      if (numDeleted > 0) {
        void queryClient.invalidateQueries({
          queryKey: [GET_SESSION_BY_ID],
        });
      }

      onEmptySessionsDeleted?.(numDeleted);
    },

    onError: (error) => {
      logger.error('Failed to delete empty sessions', {
        error: sanitizeError(error),
      });
    },
  });
}
